import {Alert} from "react-bootstrap";
import React from "react";
import {Button, getFullName} from "fearless-common-ui";
import {Course, CourseGroup, GroupCluster, GroupRole, User, UserAssignment} from 'fearless-common';
import useApiCall from "../../../components/hooks/useApiCall";
import {backendEndpoints} from "../../../libs/backendEndpoints";
import {Ban, Infinity, PlusLg} from "react-bootstrap-icons";
import {routes} from "../../../utils";
import {toast} from "react-hot-toast";

type UserWithData = User & { role: GroupRole };
type GroupWithData = CourseGroup & {
  users: UserWithData[];
  user_assignments: UserAssignment[];
};
export type CourseWithData = Course & {
  groups: GroupWithData[];
  group_clusters: GroupCluster[];
};

/**
 * Renders a table row with details for a specific group within a course. Includes information
 * about the group's name, assigned teaching assistants, current member count, and the option
 * for a user to join the group, if applicable.
 */
function TableRowForGroup({ group, course, userIdToJoin }: {
  // The group object containing group details such as users and roles.
  group: GroupWithData,
  // The course object the user tries to join.
  course: CourseWithData,
  // The ID of the user attempting to join the group.
  userIdToJoin: User['id'];
}) {
  const { isSubmitting: isJoining, error: joinError, onSubmit } = useApiCall({
    url: backendEndpoints.inviteUserToCourseGroup(group.id),
    method: 'post',
  });

  const teachingAssistants = group.users.filter(u => u.role === GroupRole.Manager);
  const students = group.users.filter(u => u.role === GroupRole.Member);
  const groupCluster = course.group_clusters.find(
    (g) => g.id === group.group_cluster_id
  );
  const isFull = course.max_students_per_group ? students.length >= course.max_students_per_group : false;
  return (
    <React.Fragment key={group.id}>
      <tr key={group.id}>
        <td>{groupCluster?.name || <i>-</i>}</td>
        <td>{group.name}</td>
        <td>
          {teachingAssistants?.length > 0
            ? <>
              {teachingAssistants.map((ta, i) => <div key={ta.id}>{getFullName(ta)}</div>)}
            </>
            : <i>-</i>}
        </td>
        <td>{students.length} / {course.max_students_per_group || <Infinity />}</td>
        <td>
          {isFull ? (
            <Button
              isDoing={isJoining}
              size="sm"
              variant="outline-light"
              disabled
              icon={<Ban/>}
            >

              Full
            </Button>
          ) : (
            <Button
              icon={<PlusLg />}
              isDoing={isJoining}
              size="sm"
              onClick={() => {
                onSubmit({ user: userIdToJoin }).then((result) => {
                  if (result) {
                    // Redirect to ELN cloud
                    window.location.href = routes.elnCloud;
                  }
                });
              }}
            >
              Join
            </Button>
          )}
        </td>
      </tr>
      {joinError && <tr><td colSpan={4}><Alert variant="danger" className="mt-2">{joinError}</Alert></td></tr>}
    </React.Fragment>
  );
}

function JoinCourseButtonAsIndividual({ userIdToJoin, course }: {
  course: CourseWithData;
  userIdToJoin: User['id'];
}) {
  const [idJoining, setIdJoining] = React.useState<number | null>(null);
  const { isSubmitting: isJoining, error: joinError, onSubmit } = useApiCall({
    url: backendEndpoints.joinCourse(course.id),
  });

  return (
    <>
      {joinError && <Alert variant="danger" className="mt-2">{joinError}</Alert>}
      <table className="table table-sm text-start border-top">
        <tbody>
        {course.group_clusters.map((cluster) => (
          <tr key={cluster.id}>
            <td width={200} valign="middle">{cluster.name}</td>
            <td width={40} className="text-center" valign="middle">
              <Button
                size="lg"
                onClick={() => {
                  if (userIdToJoin) {
                    setIdJoining(cluster.id);
                    onSubmit({ group_cluster_id: cluster.id })
                      .then((newGroup) => {
                        console.log(newGroup);
                        if (newGroup) {
                          window.location.href = routes.elnCloudGroup(newGroup.id);
                        }
                      })
                      .catch((e) => {
                        // TODO handle the error
                      })
                      .finally(() => {
                        setIdJoining(null);
                      });
                  } else {
                    toast.error('You need to be logged in to join a group.');
                  }
                }}
                isDoing={isJoining && idJoining === cluster.id}
                disabled={isJoining && idJoining !== cluster.id}
              >
                Join
              </Button>
            </td>
          </tr>
        ))}
        </tbody>
      </table>
    </>
  );
}

/**
 * Renders a table component allowing a user to select a student group to join, displaying available groups
 * and their information such as group number, teaching assistant, and current occupation.
 *
 * @return {JSX.Element} A rendered table displaying the list of student groups available to join.
 */
export default function SelectStudentGroupToJoin({ course, userIdToJoin }: {
  course: CourseWithData;
  // User that wants to join the group
  userIdToJoin: User['id'];
}) {
  if (course.groups_are_individuals) {
    return (
      <JoinCourseButtonAsIndividual
        userIdToJoin={userIdToJoin}
        course={course}
      />
    );
  }

  const clusterHashMap = course.group_clusters.reduce(
    (acc, cluster) => ({ ...acc, [cluster.id]: cluster }),
    {} as Record<GroupCluster['id'], GroupCluster>);

  const sortByGroupClusterAndGroupName = (a: CourseGroup, b: CourseGroup) => {
    const clusterA = a.group_cluster_id ? clusterHashMap[a.group_cluster_id] : null;
    const clusterB = b.group_cluster_id ? clusterHashMap[b.group_cluster_id] : null;
    const aLabel = `${clusterA?.name}-${a.name}`;
    const bLabel = `${clusterB?.name}-${b.name}`;
    return aLabel.localeCompare(bLabel);
  }

  // Display a list of group slot that students can join
  return (
    <>
      <table className="table text-sm table-sm">
        <thead>
        <tr>
          <th>Session</th>
          <th>Team</th>
          <th>Educator</th>
          <th>Seats</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
          {course.groups.sort(sortByGroupClusterAndGroupName).map((group) => {
            return (
              <TableRowForGroup
                key={group.id}
                group={group}
                course={course}
                userIdToJoin={userIdToJoin}
              />
            );
          })}
        </tbody>
      </table>
    </>
  );
}
